import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import { useSelector } from '@/api/state';
import { useTranslation } from '@/i18n';

import { getJobLineItem } from '@/utils/stateAccessors';

import Button from '@payaca/components/button/Button';
import {
  ButtonColourVariant,
  ButtonStyleVariant,
} from '@payaca/components/button/enums';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import EditJobLineItemControl from '../../components/viewEditItem/EditJobLineItemControl';
import AuthenticatedPageWrapper from '../pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';

import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';

import { Deal } from '@payaca/types/dealTypes';
import { JobLineItem } from '@payaca/types/jobContentTypes';
import { JobLineItemAttachment } from '@payaca/types/jobTypes';
import { Job } from '@payaca/types/jobTypesV2';

import { getJobType } from '@payaca/helpers/jobHelperV2';

import { useJob } from '@payaca/store/hooks/appState';

import './ItemPage.sass';

export type FormState = {
  jobLineItemId?: JobLineItem['id'];
  name?: JobLineItem['name'];
  description?: JobLineItem['description'];
  isMultipleChoice?: JobLineItem['isMultipleChoice'];
  isOptional?: JobLineItem['isOptional'];
  isSelected?: JobLineItem['isSelected'];
  quantity?: JobLineItem['quantity'];
  attachments?: JobLineItemAttachment[];
  lineItemId?: JobLineItem['lineItemId'];
  price?: JobLineItem['price'];
  cisApplies?: boolean;
  taxRateId?: JobLineItem['taxRateId'];
  discountPercentage?: JobLineItem['discountPercentage'];
  discountDescription?: JobLineItem['discountDescription'];
};
type Props = {
  jobLineItemId: number;
  jobId: Job['id'];
  dealId?: Deal['id'];
  isAlwaysRequired?: boolean;
};
const JobLineItemPage: FC<Props> = ({
  jobLineItemId,
  jobId,
  dealId,
  isAlwaysRequired = false,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [isDeletingJobLineItem, setIsDeletingJobLineItem] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const job = useJob(jobId);

  useEffect(() => {
    if (jobLineItemId && !hasLoaded) {
      dispatch(
        jobContentActions.requestGetJobLineItem(jobLineItemId, () => {
          setHasLoaded(true);
        })
      );
      dispatch(
        jobContentActions.requestGetJobLineItemAttachmentsForJobLineItem(
          jobLineItemId
        )
      );
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [jobLineItemId, hasLoaded]);

  const translate = useTranslation();

  const jobLineItem: JobLineItem | undefined = useSelector((state) =>
    getJobLineItem(state, jobLineItemId)
  );

  useEffect(() => {
    return () => {
      // leaving page - get updated job content
      if (jobLineItem?.jobContentId) {
        dispatch(
          jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
            jobLineItem.jobContentId
          )
        );
      }
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'jobLineItem.jobContentId', and 'dispatch'
  }, []);

  const deleteJobLineItem = useCallback(() => {
    if (jobLineItemId) {
      setIsDeletingJobLineItem(true);
      dispatch(
        jobContentActions.requestDeleteJobLineItem(jobLineItemId, () => {
          setIsDeletingJobLineItem(false);
          history.goBack();
        })
      );
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', and 'history'
  }, [jobLineItemId]);

  const jobType = useMemo(() => {
    if (!job) return;
    if (job.isProtoInvoice) return 'Invoice';
    return getJobType(job);
  }, [job]);

  return (
    <>
      <Helmet
        title={translate('pages.item.title', {
          reference: jobLineItem?.name || '',
        })}
      />
      <AuthenticatedPageWrapper
        className="job-line-item-page"
        breadcrumbItems={[
          {
            label: jobType ? `Back to ${jobType}` : 'Back',
            to: location.pathname.split('/items')[0],
          },
          { label: 'Item' },
        ]}
      >
        <div className="flex min-h-[70px] items-center px-8 py-4">
          <div className="flex-container flex-center grow">
            <h1>Item you sell</h1>
          </div>
          <Button
            styleVariant={ButtonStyleVariant.ANCHOR}
            onClick={deleteJobLineItem}
            isProcessing={isDeletingJobLineItem}
            colourVariant={ButtonColourVariant.RED}
          >
            Remove Item
          </Button>
        </div>
        {!jobLineItem ? (
          <div className="loading-container">
            <MiniLoader />
          </div>
        ) : (
          <EditJobLineItemControl
            jobLineItemId={jobLineItemId}
            jobId={jobId}
            dealId={dealId}
            isAlwaysRequired={isAlwaysRequired}
          />
        )}
      </AuthenticatedPageWrapper>
    </>
  );
};
export default JobLineItemPage;
